import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';
import type {
  AlarmRuleDto,
  createDeviceSchema,
  createIndicatorSchema,
  DataLayer,
  DeviceCategoryDto,
  DeviceDto,
  DeviceLocationDto,
  FileDto,
  FormulaPreviewResponse,
  IndicatorDto,
  SensorHistoryResponse,
  Thresholds,
} from '@atnt/shared';
import { api } from '@/shared/api/client';

/**
 * Device registry API hooks (apps/api/src/modules/devices/routes.ts, readings, alarms, files).
 * `tenantId` is the tenant UUID or slug; keys follow `['tenant', tenantId, <resource>, …]`.
 */

const base = (tenantId: string) => `/tenants/${encodeURIComponent(tenantId)}`;

export const deviceKeys = {
  categories: (tenantId: string) => ['tenant', tenantId, 'device-categories'] as const,
  locations: (tenantId: string) => ['tenant', tenantId, 'device-locations'] as const,
  devices: (tenantId: string) => ['tenant', tenantId, 'devices'] as const,
  sensors: (tenantId: string) => ['tenant', tenantId, 'sensors'] as const,
  history: (tenantId: string, indicatorId: string, range: string) => ['tenant', tenantId, 'sensor-history', indicatorId, range] as const,
  alarmRules: (tenantId: string) => ['tenant', tenantId, 'alarm-rules'] as const,
};

/* ───────────────────────── queries ───────────────────────── */

export const categoriesQuery = (tenantId: string) =>
  queryOptions({
    queryKey: deviceKeys.categories(tenantId),
    queryFn: () => api.get<DeviceCategoryDto[]>(`${base(tenantId)}/device-categories`),
    staleTime: 30_000,
  });

export const locationsQuery = (tenantId: string) =>
  queryOptions({
    queryKey: deviceKeys.locations(tenantId),
    queryFn: () => api.get<DeviceLocationDto[]>(`${base(tenantId)}/device-locations`),
    staleTime: 30_000,
  });

export const devicesQuery = (tenantId: string) =>
  queryOptions({
    queryKey: deviceKeys.devices(tenantId),
    queryFn: () => api.get<DeviceDto[]>(`${base(tenantId)}/devices`),
    staleTime: 30_000,
  });

export const useCategories = (tenantId: string, enabled = true) => useQuery({ ...categoriesQuery(tenantId), enabled });
export const useLocations = (tenantId: string, enabled = true) => useQuery({ ...locationsQuery(tenantId), enabled });
export const useDevices = (tenantId: string, enabled = true) => useQuery({ ...devicesQuery(tenantId), enabled });

export type HistoryRange = DataLayer['defaultRange'];

/** One indicator's stored history (`GET /sensors/:id/history`). */
export function useIndicatorHistory(
  tenantId: string,
  indicatorId: string | null | undefined,
  range: HistoryRange,
  opts: { bucket?: 'auto' | 'raw'; maxPoints?: number; enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [...deviceKeys.history(tenantId, indicatorId ?? '', range), opts.bucket ?? 'auto', opts.maxPoints ?? 500],
    queryFn: () =>
      api.get<SensorHistoryResponse>(`${base(tenantId)}/sensors/${encodeURIComponent(indicatorId!)}/history`, {
        query: { range, bucket: opts.bucket ?? 'auto', maxPoints: opts.maxPoints ?? 500 },
      }),
    enabled: Boolean(indicatorId) && opts.enabled !== false,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

/* ───────────────────────── mutations ───────────────────────── */

type CategoryInput = { name: string; icon?: string | null; color?: string | null };
type LocationInput = { name: string };
export type DeviceInput = z.input<typeof createDeviceSchema>;
export type IndicatorInput = z.input<typeof createIndicatorSchema>;

/** Every registry write refreshes the lists that embed the changed row (counts, names, indicators, sensors). */
function useInvalidateRegistry(tenantId: string) {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: deviceKeys.categories(tenantId) }),
      qc.invalidateQueries({ queryKey: deviceKeys.locations(tenantId) }),
      qc.invalidateQueries({ queryKey: deviceKeys.devices(tenantId) }),
      qc.invalidateQueries({ queryKey: deviceKeys.sensors(tenantId) }),
    ]);
}

export function useSaveCategory(tenantId: string) {
  const invalidate = useInvalidateRegistry(tenantId);
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: CategoryInput }) =>
      id
        ? api.patch<DeviceCategoryDto>(`${base(tenantId)}/device-categories/${encodeURIComponent(id)}`, input)
        : api.post<DeviceCategoryDto>(`${base(tenantId)}/device-categories`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteCategory(tenantId: string) {
  const invalidate = useInvalidateRegistry(tenantId);
  return useMutation({
    mutationFn: (id: string) => api.delete(`${base(tenantId)}/device-categories/${encodeURIComponent(id)}`),
    onSuccess: invalidate,
  });
}

export function useSaveLocation(tenantId: string) {
  const invalidate = useInvalidateRegistry(tenantId);
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: LocationInput }) =>
      id
        ? api.patch<DeviceLocationDto>(`${base(tenantId)}/device-locations/${encodeURIComponent(id)}`, input)
        : api.post<DeviceLocationDto>(`${base(tenantId)}/device-locations`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteLocation(tenantId: string) {
  const invalidate = useInvalidateRegistry(tenantId);
  return useMutation({
    mutationFn: (id: string) => api.delete(`${base(tenantId)}/device-locations/${encodeURIComponent(id)}`),
    onSuccess: invalidate,
  });
}

export function useSaveDevice(tenantId: string) {
  const invalidate = useInvalidateRegistry(tenantId);
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: Partial<DeviceInput> }) =>
      id
        ? api.patch<DeviceDto>(`${base(tenantId)}/devices/${encodeURIComponent(id)}`, input)
        : api.post<DeviceDto>(`${base(tenantId)}/devices`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteDevice(tenantId: string) {
  const invalidate = useInvalidateRegistry(tenantId);
  return useMutation({
    mutationFn: (id: string) => api.delete(`${base(tenantId)}/devices/${encodeURIComponent(id)}`),
    onSuccess: invalidate,
  });
}

export function useSaveIndicator(tenantId: string) {
  const invalidate = useInvalidateRegistry(tenantId);
  return useMutation({
    mutationFn: ({ deviceId, id, input }: { deviceId: string; id?: string; input: Partial<IndicatorInput> }) =>
      id
        ? api.patch<IndicatorDto>(`${base(tenantId)}/devices/${encodeURIComponent(deviceId)}/indicators/${encodeURIComponent(id)}`, input)
        : api.post<IndicatorDto>(`${base(tenantId)}/devices/${encodeURIComponent(deviceId)}/indicators`, input),
    onSuccess: invalidate,
  });
}

export function useDeleteIndicator(tenantId: string) {
  const invalidate = useInvalidateRegistry(tenantId);
  return useMutation({
    mutationFn: ({ deviceId, id }: { deviceId: string; id: string }) =>
      api.delete(`${base(tenantId)}/devices/${encodeURIComponent(deviceId)}/indicators/${encodeURIComponent(id)}`),
    onSuccess: invalidate,
  });
}

/** "Mezon" switch of an indicator (`PUT /alarm-rules/:indicatorId`, admin devices.update). */
export function useSaveAlarmRule(tenantId: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidateRegistry(tenantId);
  return useMutation({
    mutationFn: ({ indicatorId, input }: { indicatorId: string; input: { alarmEnabled?: boolean; thresholds?: Thresholds | null } }) =>
      api.put<AlarmRuleDto>(`${base(tenantId)}/alarm-rules/${encodeURIComponent(indicatorId)}`, input),
    onSuccess: () => Promise.all([invalidate(), qc.invalidateQueries({ queryKey: deviceKeys.alarmRules(tenantId) })]),
  });
}

/** Upload a passport (`POST /tenants/:t/files?kind=document`, multipart field `file`). */
export function useUploadDocument(tenantId: string) {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.post<FileDto>(`${base(tenantId)}/files`, form, { query: { kind: 'document' } });
    },
  });
}

/** Live formula check (`POST /formulas/preview`): value for the source's latest reading or the error span. */
export function previewFormula(tenantId: string, input: { formula: string; sourceIndicatorId?: string | null; x?: number | null }) {
  return api.post<FormulaPreviewResponse>(`${base(tenantId)}/formulas/preview`, input);
}
